import React, { useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { WizardStepper } from "../../shared/ui";
import Grid from "@mui/material/Grid";
import { useParams } from "react-router-dom";
import { GenerateReportPageSkeleton } from "../../components/skeletons/GenerateReportPageSkeleton";
import { useAuth } from "@workos-inc/authkit-react";
import { stationTemplatesQuery } from "../../features/templates/api/templateQueries";
import { ShareEvidenceModal } from "../../features/evidence/components/ShareEvidenceModal";
import { useOrgId } from "../../shared/auth/orgContext";
import { canManageEvidenceShares } from "../../shared/auth/authorization";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { NarrativeTemplate } from "../../shared/interfaces/ReportGenReport";
import { DEFAULT_REPORT_TYPE, mergeReportTypes } from "./builtInTemplates";
import { VideoStatus } from "../../shared/interfaces/VideoData";
import { useJobStore } from "../../store/jobStore";
import { useToast } from "../../shared/toast";
import { useVideoData, useReportGeneration, useTranscription } from "./hooks";
import {
  VideoEvidenceSection,
  ReportConfigSection,
  GenerateReportError,
  TranscribeVideosDialog,
  AdditionalContextDialog,
} from "./components";
import { usePageHeader } from "../../context/LayoutContext";
import BoltIcon from "@mui/icons-material/Bolt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const GenerateReportPage: React.FC = () => {
  const { evidenceID } = useParams();
  const isMaxJobsReached = useJobStore((state) => state.isMaxJobsReached());
  const organizationId = useOrgId();
  // Usually served from the cache MainComponent's eager observer warmed at
  // login; while it loads the picker shows the built-ins on their own.
  const { data: stationTemplates } = useQuery({
    ...stationTemplatesQuery(organizationId ?? ""),
    enabled: !!organizationId,
  });
  const reportTypes = useMemo(() => mergeReportTypes(stationTemplates ?? []), [stationTemplates]);
  const toast = useToast();

  // Selection is held by id, not by object: the templates arrive after first
  // paint, and the fetched copy of a built-in is what carries its example
  // narrative, so the picked template has to be re-read from the current list.
  const [reportTypeId, setReportTypeId] = useState(DEFAULT_REPORT_TYPE.template.templateId);
  const reportType = useMemo(
    () => reportTypes.find((type) => type.template.templateId === reportTypeId) ?? DEFAULT_REPORT_TYPE,
    [reportTypes, reportTypeId]
  );
  // Free-text details the officer adds for the agent; collected by the
  // pre-generation dialog and sent with the job.
  const [additionalContext, setAdditionalContext] = useState("");
  const [contextOpen, setContextOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const permissions = usePermissions();
  const { user } = useAuth();

  const {
    videoData,
    videoDataList,
    currentPage,
    viewedVideos,
    loading,
    viewedCount,
    totalVideos,
    generateDisabled,
    failedVideoCount,
    pendingVideoCount,
    handlePageChange,
    handleNextVideo,
    handlePrevVideo,
  } = useVideoData(evidenceID, toast.error);

  const { isGenerating, handleSubmitClick } = useReportGeneration(
    evidenceID,
    reportType,
    videoDataList,
    isMaxJobsReached,
    toast.error,
    additionalContext
  );

  const {
    isTranscribing,
    selectOpen,
    viewOpen,
    results,
    handleTranscribeClick,
    startTranscription,
    goToTranscript,
    closeSelect,
    closeView,
  } = useTranscription(videoDataList, videoData, toast.error, toast.info);

  const handleReportTypeChange = (value: NarrativeTemplate) => {
    setReportTypeId(value.template.templateId);
  };

  // The Transcribe dialog works over the whole `videoDataList` selection, so keep
  // the button available while any video still needs a transcript — not just the
  // currently-viewed one. Hide it once every video is transcript-ready, and
  // don't count videos with no file yet (no videoUrl) — they can't transcribe.
  const showTranscribeButton =
    !!videoData &&
    videoDataList.some(
      (video) => !!video.videoUrl && video.status !== VideoStatus.TRANSCRIPT_READY,
    );

  // Same gate the share endpoints enforce: uploader or station-wide reader.
  // Sharing acts on the currently-viewed video only.
  const canShare =
    !!videoData &&
    canManageEvidenceShares(
      permissions,
      !!videoData.uploadedBy && videoData.uploadedBy === user?.id,
    );

  // Set page header in app bar
  usePageHeader({
    title: "Generate Report",
    subtitle: "Review video evidence and create incident reports",
    icon: <BoltIcon />,
    chip: { label: `${videoDataList.length} ${videoDataList.length === 1 ? 'Video' : 'Videos'}`, icon: <PlayArrowIcon /> },
  });

  return (
    <Container sx={{ minWidth: { xs: "200px", sm: "450px" }, overflow: "auto" }}>
      {/* Stepper + back button stay mounted through loading and error states so
          the officer keeps the wizard context and can always return. */}
      <WizardStepper
        activeStep={1}
        // The evidence selection itself is in-memory and does not survive
        // the round trip; the URL still carries these videos forward.
        back={{ label: "Evidence selection", to: "/video-selection" }}
        sx={{ mt: 2 }}
      />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <GenerateReportPageSkeleton />
        </Box>
      ) : !videoData ? (
        <GenerateReportError onRetry={() => window.location.reload()} />
      ) : (
        <Grid container spacing={3} direction="column" sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <VideoEvidenceSection
                  videoData={videoData as NonNullable<typeof videoData>}
                  videoDataListLength={videoDataList.length}
                  currentPage={currentPage}
                  viewedVideos={viewedVideos}
                  handlePageChange={handlePageChange}
                  handlePrevVideo={handlePrevVideo}
                  handleNextVideo={handleNextVideo}
                  onShare={canShare ? () => setShareOpen(true) : undefined}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <ReportConfigSection
                  reportType={reportType}
                  reportTypes={reportTypes}
                  onReportTypeChange={handleReportTypeChange}
                  onGenerate={() => setContextOpen(true)}
                  isGenerating={isGenerating}
                  isMaxJobsReached={isMaxJobsReached}
                  generateDisabled={generateDisabled}
                  failedVideoCount={failedVideoCount}
                  pendingVideoCount={pendingVideoCount}
                  viewedCount={viewedCount}
                  totalVideos={totalVideos}
                  onTranscribe={handleTranscribeClick}
                  isTranscribing={isTranscribing}
                  transcribeDisabled={isGenerating || isTranscribing}
                  showTranscribeButton={showTranscribeButton}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {videoData && (
        <ShareEvidenceModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          evidenceId={videoData.id}
          videoTitle={videoData.title}
        />
      )}

      {/* The job starts from here, not from the Generate button: the button only
          opens this dialog. A successful submit navigates away, so the dialog
          never needs closing on its own. */}
      <AdditionalContextDialog
        open={contextOpen}
        onClose={() => setContextOpen(false)}
        additionalContext={additionalContext}
        onAdditionalContextChange={setAdditionalContext}
        onGenerate={handleSubmitClick}
        isGenerating={isGenerating}
      />

      <TranscribeVideosDialog
        selectOpen={selectOpen}
        onCloseSelect={closeSelect}
        videos={videoDataList}
        onConfirm={startTranscription}
        viewOpen={viewOpen}
        onCloseView={closeView}
        results={results}
        onView={goToTranscript}
      />
    </Container>
  );
};

export default GenerateReportPage;
export { GenerateReportPage };
